﻿import React, { CSSProperties, useEffect, useRef, useState } from 'react';
import ReactHtmlParser from 'html-react-parser';
import { observer } from 'mobx-react';

import { Space, Tag, TagProps } from 'antd';

import Map from 'ol/Map';
import Control from 'ol/control/Control';
import { FrameState } from 'ol/Map';
import { State as LayerState } from 'ol/layer/Layer';
import { State as ViewState } from 'ol/View';

import { Utilities } from "msg-ui-react/dist/utils/Utilities";

export interface Props extends Pick<TagProps, "color"> {
    map: Map | undefined,
    id?: string,
    className?: string,
    style?: CSSProperties,
    mobile?: boolean
}

const MapAttributionField: React.FC<Props> = ({
    map,
    id,
    className = "map-attribution-field",
    style,
    color = "default",
    mobile,
}) => {

    const element = useRef<HTMLDivElement>(null);
    const container = useRef<HTMLDivElement>(null);
    const attributionsRef = useRef<string[]>([]);

    const [attributions, setAttributions] = useState<string[]>([]);

    useEffect(() => {
        attributionsRef.current = attributions;
    }, [attributions]);

    useEffect(() => {
        if (map && element.current && container.current) {

            const control = new Control({
                target: container.current,
                element: element.current,
                render: evt => {
                    const newAttributions = getAttributes(evt.frameState ?? undefined);
                    if (!Utilities.compareArrays(newAttributions, attributionsRef.current))
                        setAttributions(newAttributions);
                }
            })

            map.addControl(control);

            return () => {
                try {
                    map.removeControl(control);
                }
                catch { }
            }
        }
    }, [map]);

    return (
        <Tag color={color} id={id} className={className} ref={container}
            style={{
                pointerEvents: 'auto',
                position: 'absolute',
                display: (mobile || !attributions.length) ? 'none' : undefined,
                ...style
            }}>
            <div ref={element}>
                <Space size={0}>
                    {
                        attributions.map((attribution, i) => {
                            return <span key={i}>{ReactHtmlParser(attribution)}</span>
                        })
                    }
                </Space>
            </div>
        </Tag>
    )
}

export default observer(MapAttributionField);

//#region Internal

function inView(layerState: LayerState, viewState: ViewState) {

    if (!layerState.visible)
        return false;

    const resolution = viewState.resolution;
    if (resolution < layerState.minResolution || resolution >= layerState.maxResolution)
        return false;

    const zoom = viewState.zoom;
    return zoom > layerState.minZoom && zoom <= layerState.maxZoom;
}

function getAttributes(frameState?: FrameState) {

    if (!frameState)
        return [];

    const lookup: { [key: string]: boolean } = {};

    const visibleAttributions = [];

    let collapsible = true;
    const layerStatesArray = frameState.layerStatesArray;
    for (let i = 0, ii = layerStatesArray.length; i < ii; ++i) {
        const layerState = layerStatesArray[i];
        if (!inView(layerState, frameState.viewState))
            continue;

        const source = layerState.layer.getSource();
        if (!source)
            continue;

        const attributionGetter = source.getAttributions();
        if (!attributionGetter)
            continue;

        const attributions = attributionGetter(frameState);
        if (!attributions)
            continue;

        collapsible = collapsible && source.getAttributionsCollapsible() !== false;

        if (Array.isArray(attributions)) {
            for (let j = 0, jj = attributions.length; j < jj; ++j) {
                if (!(attributions[j] in lookup)) {
                    visibleAttributions.push(attributions[j]);
                    lookup[attributions[j]] = true;
                }
            }
        } else {
            if (!(attributions in lookup)) {
                visibleAttributions.push(attributions);
                lookup[attributions] = true;
            }
        }
    }

    return visibleAttributions;
}

//#endregion
